import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import EAvatar from '@/components/everridge/EAvatar';
import { cn } from '@/lib/utils';
import { OwnerDetailsResponseDto } from '@/features/owners/types/owners-dto';
import { Checkbox } from '@/components/ui/checkbox';
import EDateText from '@/components/everridge/EDateText';
import { ApiConfig } from '@/config/api.config';

const OwnersInvitationColumns = (): ColumnDef<OwnerDetailsResponseDto>[] => {
    const { t } = useTranslation();

    const columnClasses = 'text-sm text-popover-800';

    return [
        {
            id: 'select',
            header: ({ table }) => (
                <div className="flex items-center justify-center">
                    <Checkbox
                        checked={table.getIsAllRowsSelected()}
                        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
                        className="cursor-pointer"
                    />
                </div>
            ),
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        onClick={(e) => e.stopPropagation()}
                        className="px-3 py-4 cursor-pointer"
                    />
                </div>
            ),
        },
        {
            accessorKey: 'name',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
                        }
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                    >
                        {t('main.owners.components.invitation.table.header.name')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={cn('flex items-center gap-2.5 truncate', columnClasses)}>
                    <EAvatar
                        className={'size-8'}
                        url={
                            row.original.avatarUrl
                                ? `${ApiConfig.currentUrls.api.slice(0, -1)}${
                                      row.original.avatarUrl
                                  }`
                                : undefined
                        }
                    />
                    <span className="truncate">{row.getValue('name')}</span>
                </div>
            ),
        },
        {
            accessorKey: 'email',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
                        }
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                    >
                        {t('main.owners.components.invitation.table.header.email')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={cn('flex items-center gap-2.5 truncate', columnClasses)}>
                    <span className="truncate">{row.getValue('email')}</span>
                </div>
            ),
        },
        {
            accessorKey: 'invitedAt',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
                        }
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                    >
                        {t('main.owners.components.invitation.table.header.invitedAt')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={cn('w-32 flex items-center gap-2.5 truncate', columnClasses)}>
                    <EDateText date={row.getValue('invitedAt')} />
                </div>
            ),
        },
    ];
};

export default OwnersInvitationColumns;
